import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users,
  Download,
  Search,
  Phone,
  Mail,
  Calendar,
  Filter,
  RefreshCw,
  AlertCircle,
  Loader2,
  ChevronDown,
  ArrowUpDown,
  Check,
  ChevronUp
} from 'lucide-react';
import { api } from '../../services/api';
import EmptyState from '../../components/EmptyState';

export default function Leads() {
  const { id } = useParams();
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [expandedInterests, setExpandedInterests] = useState({});

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'name', label: 'By Name' },
  ];

  const fetchLeads = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getLeads(id, {
        search: searchQuery,
        tags: selectedTags.join(','),
        sort: sortBy
      });
      setLeads(response.leads || []);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [id, searchQuery, selectedTags, sortBy]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchLeads();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchLeads]);

  // Get all unique tags from current leads
  const allTags = [...new Set(leads.flatMap(lead => lead.tags || []))];

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const exportCSV = () => {
    const headers = ['Name', 'Phone', 'Email', 'Interest', 'Created At', 'Source'];
    const rows = leads.map(lead => [
      lead.name || '',
      // Prefix with apostrophe to force Excel to treat as text, not number
      lead.phone ? `'+${lead.phone.replace(/^\+/, '')}` : '',
      lead.email || '',
      lead.notes || '',
      formatDate(lead.createdAt),
      lead.source || 'WhatsApp'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Show full-page loading spinner for initial load
  if (isLoading && leads.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Leads</h1>
            <p className="text-text-secondary">{leads.length} total leads captured</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchLeads}
            disabled={isLoading}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-5 h-5 text-text-muted ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={exportCSV}
            disabled={leads.length === 0}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchLeads}
            className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Content */}
      {leads.length === 0 && !searchQuery && selectedTags.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No leads captured yet"
          description="When customers share their contact information through your AI assistant, they'll appear here."
        />
      ) : (
        <>
          {/* Filters */}
          <div className="bg-white rounded-xl border border-border p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field pl-10"
                />
              </div>

              {/* Sort Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-border rounded-lg
                             hover:border-primary/30 transition-colors text-sm"
                >
                  <ArrowUpDown className="w-3.5 h-3.5 text-primary" />
                  <span className="font-medium text-text-primary">
                    {sortOptions.find(opt => opt.value === sortBy)?.label}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-text-muted transition-transform ${showSortDropdown ? 'rotate-180' : ''}`} />
                </button>

                {showSortDropdown && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-elevated border border-border z-50 overflow-hidden animate-slide-up">
                    <div className="p-2">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => {
                            setSortBy(option.value);
                            setShowSortDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left rounded-lg text-sm flex items-center justify-between
                                     hover:bg-gray-50 transition-colors
                                     ${sortBy === option.value ? 'bg-primary/5 text-primary' : 'text-text-primary'}`}
                        >
                          <span>{option.label}</span>
                          {sortBy === option.value && <Check className="w-4 h-4" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tags Filter */}
            {allTags.length > 0 && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <Filter className="w-4 h-4 text-text-muted" />
                <span className="text-sm text-text-muted">Filter by tag:</span>
                <div className="flex flex-wrap gap-2">
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`
                        px-3 py-1 rounded-full text-sm font-medium transition-all
                        ${selectedTags.includes(tag)
                          ? 'bg-primary text-white'
                          : 'bg-background text-text-secondary hover:bg-primary/10 hover:text-primary'
                        }
                      `}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                {selectedTags.length > 0 && (
                  <button
                    onClick={() => setSelectedTags([])}
                    className="text-sm text-primary hover:underline ml-2"
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Leads Table */}
          <div className="bg-white rounded-xl border border-border overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-border">
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">Name</th>
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">Phone</th>
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">Email</th>
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider w-[180px]">Interest</th>
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider whitespace-nowrap">Captured</th>
                    <th className="text-left px-5 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {leads.map((lead, index) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-primary/5 transition-all duration-200 animate-slide-up group"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0 shadow-sm">
                            <span className="font-semibold text-white text-sm">
                              {(lead.name || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-text-primary text-sm truncate max-w-[140px] group-hover:text-primary transition-colors" title={lead.name || 'Unknown'}>
                            {lead.name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 text-sm text-text-secondary whitespace-nowrap">
                          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                            <Phone className="w-3.5 h-3.5 text-blue-500" />
                          </div>
                          <span className="truncate font-medium">{lead.phone ? `+${lead.phone.replace(/^\+/, '')}` : '-'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        {lead.email ? (
                          <div className="flex items-center gap-2 text-sm text-text-secondary">
                            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                              <Mail className="w-3.5 h-3.5 text-green-500" />
                            </div>
                            <span className="truncate max-w-[150px] font-medium" title={lead.email}>{lead.email}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-text-muted italic">Not provided</span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {lead.notes ? (
                          <div className="max-w-[220px]">
                            <p
                              className={`text-sm text-text-secondary leading-relaxed break-words ${
                                expandedInterests[lead.id] ? '' : 'overflow-hidden'
                              }`}
                              style={expandedInterests[lead.id] ? {} : {
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {lead.notes}
                            </p>
                            {lead.notes.length > 50 && (
                              <button
                                onClick={() => setExpandedInterests(prev => ({
                                  ...prev,
                                  [lead.id]: !prev[lead.id]
                                }))}
                                className="text-xs text-primary hover:text-primary/80 font-medium mt-1.5 flex items-center gap-0.5 hover:underline"
                              >
                                {expandedInterests[lead.id] ? (
                                  <>Show less <ChevronUp className="w-3 h-3" /></>
                                ) : (
                                  <>Show more <ChevronDown className="w-3 h-3" /></>
                                )}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-text-muted italic">No interest noted</span>
                        )}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                            <Calendar className="w-3.5 h-3.5 text-purple-500" />
                          </div>
                          <span className="font-medium">{formatDate(lead.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                          {lead.source || 'WhatsApp'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {leads.length === 0 && (searchQuery || selectedTags.length > 0) && (
              <div className="text-center py-12">
                <p className="text-text-muted">No leads match your filters</p>
                <button
                  onClick={() => { setSearchQuery(''); setSelectedTags([]); }}
                  className="text-primary hover:underline mt-2"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {/* Results count */}
          <p className="text-sm text-text-muted mt-4">
            Showing {leads.length} leads
          </p>
        </>
      )}
    </div>
  );
}